import { useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';

type AuditTableStatus = 'draft' | 'reviewed' | 'published';
type AuditStepId = 'draft' | 'reviewed' | 'published';

export type AuditTableLabels = {
    readonly ariaLabel: string;
    readonly columnStatus: string;
    readonly columnUser: string;
    readonly columnWhen: string;
    readonly noPublisher: string;
    readonly pending: string;
    readonly unknownUser: string;
    readonly allStepLabels: readonly string[];
    readonly labelForStep: (stepId: AuditStepId) => string;
};

export const useAuditTableLabels = (status: AuditTableStatus): AuditTableLabels => {
    const translate = useTranslate();

    const draftedLabel = translate('pages.job-architecture-v2.audit-table.row.drafted');
    const reviewLabel = translate('pages.job-architecture-v2.audit-table.row.review');
    const reviewedLabel = translate('pages.job-architecture-v2.audit-table.row.reviewed');
    const publishLabel = translate('pages.job-architecture-v2.audit-table.row.publish');
    const publishedLabel = translate('pages.job-architecture-v2.audit-table.row.published');

    const allStepLabels = useMemo(
        () => [draftedLabel, reviewLabel, reviewedLabel, publishLabel, publishedLabel] as const,
        [draftedLabel, reviewLabel, reviewedLabel, publishLabel, publishedLabel],
    );

    const labelForStep = (stepId: AuditStepId): string => {
        if (stepId === 'draft') {
            return draftedLabel;
        }
        if (stepId === 'reviewed') {
            return status === 'draft' ? reviewLabel : reviewedLabel;
        }
        return status === 'published' ? publishedLabel : publishLabel;
    };

    return {
        allStepLabels,
        ariaLabel: translate('pages.job-architecture-v2.audit-table.aria-label'),
        columnStatus: translate('pages.job-architecture-v2.audit-table.column.status'),
        columnUser: translate('pages.job-architecture-v2.audit-table.column.user'),
        columnWhen: translate('pages.job-architecture-v2.audit-table.column.when'),
        labelForStep,
        noPublisher: translate('pages.job-architecture-v2.audit-table.no-publisher'),
        pending: translate('pages.job-architecture-v2.audit-table.pending'),
        unknownUser: translate('pages.job-architecture-v2.job-profile-unknown-user'),
    };
};
